import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import MicNoneIcon from '@mui/icons-material/MicNone';
import { API_BASE_URL, getAuthHeaders } from '../auth';
import AreDisclaimer from '../AreDisclaimer';
import { TURN_URL } from './api';
import { FIELDS, areaOf, blockSpec, lengthLabel, missingFields, optionLabel, scheduleLabel } from './cardModel';
import '../AdvisorVoiceDialog.css';
import './audio.css';

/**
 * Speech-to-speech overlay in which Bro builds an audio card.
 *
 * Same state machine and look as EfdVoiceDialog (shared `av-*` styling):
 * listen with voice-activity detection → POST the audio → stream transcript /
 * text / audio → play the audio queue → listen again; a tap while Bro thinks
 * or speaks interrupts (barge-in). Bro greets first.
 *
 * What is new: the backend (src/audio_cards/builder.py) sends the card it
 * built as its own `card` event — shown under the status like the collected
 * fields of the Client Notes voice capture (progress + chips) and handed to
 * the form behind via `onDraft`. "Spiel mal ab" streams the briefing itself
 * (text + audio), "speichern" stores the card (`saved`) and the dialog closes
 * once Bro has said so. Quick replies and a text field let the user answer
 * without speaking (also the fallback without a microphone).
 */

// Voice-activity-detection settings (same values as the EFD and ARE dialogs).
const VAD_THRESHOLD = 0.025;
const MIN_SPEECH_MS = 400;
const SILENCE_MS = 1700;
const MAX_CONSECUTIVE_FAILURES = 3;
const NO_SPEECH_TIMEOUT_MS = 10000;
const MAX_RECORDING_MS = 30000;

function chipsOf(draft, catalog, sources, t) {
  const out = [];
  if (draft.name) out.push(['name', `${t('audio_field_name')}: ${draft.name}`]);
  const area = areaOf(draft, sources);
  if (draft.scope?.type && area) out.push(['scope', `${t('audio_field_scope')}: ${area}`]);
  (draft.blocks || []).forEach((b, i) => {
    const spec = blockSpec(catalog, b.key);
    const opt = spec && spec.options.length > 1 ? ` (${optionLabel(catalog, b.key, b.option)})` : '';
    out.push([`b:${b.key}:${b.option}`, `${i + 1} · ${spec?.label || b.key}${opt}`]);
  });
  if (draft.length) out.push(['length', `${t('audio_field_length')}: ${lengthLabel(catalog, draft.length)}`]);
  if (draft.schedule) out.push(['schedule', `${t('audio_field_schedule')}: ${scheduleLabel(catalog, draft.schedule)}`]);
  return out;
}

export default function AudioBuilderVoice({ draft, cardId, catalog, sources, language = 'de', greeting, quickStart = [],
  onDraft, onSaved, onClose }) {
  const { t } = useTranslation();
  const [voiceState, setVoiceState] = useState('initializing');
  const [statusText, setStatusText] = useState('');
  const [responseText, setResponseText] = useState('');
  const [messages, setMessages] = useState([]);
  const [audioLevel, setAudioLevel] = useState(0);
  const [quick, setQuick] = useState(quickStart);
  const [fresh, setFresh] = useState(() => new Set());
  const [shown, setShown] = useState(draft);
  const [typed, setTyped] = useState('');
  const [micDenied, setMicDenied] = useState(false);
  const micDeniedRef = useRef(false);   // read inside callbacks created before the denial
  const transcriptRef = useRef(null);

  const mediaStreamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const vadFrameIdRef = useRef(null);
  const speechStartTimeRef = useRef(null);
  const silenceStartTimeRef = useRef(null);
  const listenStartTimeRef = useRef(null);
  const recordingTimeoutRef = useRef(null);
  const voiceStoppedByUserRef = useRef(false);
  const consecutiveFailuresRef = useRef(0);
  const abortControllerRef = useRef(null);

  // TTS playback queue
  const ttsAudioContextRef = useRef(null);
  const currentSourceRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const streamDoneRef = useRef(false);
  const botResponseRef = useRef('');
  const startListeningRef = useRef(null);
  const pendingTurnRef = useRef(null);
  const savedRef = useRef(null);

  // The conversation and the card so far — refs, so the next turn sends them
  // without waiting for a re-render.
  const historyRef = useRef([]);
  const draftRef = useRef(draft);

  /** Close out a turn ONCE IT HAS BEEN SPOKEN: the answer moves from under the
   *  blob into the transcript. Then either the dialog ends (card saved) or it
   *  listens again. */
  const finishTurn = useCallback(() => {
    const turn = pendingTurnRef.current;
    pendingTurnRef.current = null;
    if (turn) {
      if (turn.answer) setMessages((prev) => [...prev, { role: 'assistant', text: turn.answer }]);
      historyRef.current = [
        ...historyRef.current,
        ...(turn.question ? [{ role: 'user', content: turn.question }] : []),
        ...(turn.say || turn.answer ? [{ role: 'assistant', content: turn.say || turn.answer }] : []),
      ];
    }
    setResponseText('');
  }, []);

  const stopVoice = useCallback(() => {
    voiceStoppedByUserRef.current = true;
    if (vadFrameIdRef.current) cancelAnimationFrame(vadFrameIdRef.current);
    if (recordingTimeoutRef.current) { clearTimeout(recordingTimeoutRef.current); recordingTimeoutRef.current = null; }
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    if (mediaStreamRef.current) { mediaStreamRef.current.getTracks().forEach((tr) => tr.stop()); mediaStreamRef.current = null; }
    if (audioContextRef.current) { try { audioContextRef.current.close(); } catch (e) { /* noop */ } audioContextRef.current = null; }
    if (ttsAudioContextRef.current) { try { ttsAudioContextRef.current.close(); } catch (e) { /* noop */ } ttsAudioContextRef.current = null; }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
  }, []);

  /** After a turn: the card was saved → hand over and close; else listen. */
  const afterTurn = useCallback(() => {
    if (savedRef.current) {
      const saved = savedRef.current;
      savedRef.current = null;
      stopVoice();
      if (onSaved) onSaved(saved.card, saved.created);
      return;
    }
    setVoiceState('idle');
    if (!voiceStoppedByUserRef.current && startListeningRef.current) {
      setTimeout(() => startListeningRef.current && startListeningRef.current(), 300);
    } else {
      setStatusText(micDeniedRef.current ? t('audio_voice_type') : t('audio_voice_tap'));
    }
  }, [onSaved, stopVoice, t]);

  const playNextInQueue = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      if (streamDoneRef.current) {
        finishTurn();
        afterTurn();
      }
      return;
    }
    isPlayingRef.current = true;
    const audioBase64 = audioQueueRef.current.shift();
    try {
      if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') {
        ttsAudioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = ttsAudioContextRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      const binaryString = atob(audioBase64);
      const arrayBuffer = new ArrayBuffer(binaryString.length);
      const bytes = new Uint8Array(arrayBuffer);
      for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
      ctx.decodeAudioData(arrayBuffer).then((buffer) => {
        if (!ttsAudioContextRef.current || ttsAudioContextRef.current.state === 'closed') return;
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.onended = () => { currentSourceRef.current = null; playNextInQueue(); };
        currentSourceRef.current = source;
        source.start(0);
        setVoiceState('speaking');
      }).catch((err) => { console.error('Audio decode error:', err); playNextInQueue(); });
    } catch (err) {
      console.error('Playback error:', err);
      playNextInQueue();
    }
  }, [finishTurn, afterTurn]);

  /** The card event: the form behind follows, new chips light up. */
  const applyCard = useCallback((next) => {
    const before = new Set(chipsOf(draftRef.current, catalog, sources, t).map(([k, label]) => `${k}|${label}`));
    draftRef.current = next;
    setShown(next);
    setFresh(new Set(chipsOf(next, catalog, sources, t).filter(([k, label]) => !before.has(`${k}|${label}`)).map(([k]) => k)));
    if (onDraft) onDraft(next);
  }, [catalog, sources, t, onDraft]);

  const sendTurn = useCallback(async ({ blob, text }) => {
    // Hard-stop the previous turn first, otherwise two playback chains overlap.
    if (abortControllerRef.current) { try { abortControllerRef.current.abort(); } catch (e) { /* noop */ } }
    if (currentSourceRef.current) { try { currentSourceRef.current.stop(); } catch (e) { /* noop */ } currentSourceRef.current = null; }
    setVoiceState('thinking');
    setStatusText(t('audio_voice_thinking'));
    setQuick([]);
    botResponseRef.current = '';
    streamDoneRef.current = false;
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    let questionText = text || '';

    const formData = new FormData();
    if (blob) formData.append('audio', blob, 'audio.webm');
    if (text) formData.append('text', text);
    formData.append('language', language);
    formData.append('history', JSON.stringify(historyRef.current));
    formData.append('draft', JSON.stringify(draftRef.current));
    if (cardId) formData.append('card_id', cardId);

    abortControllerRef.current = new AbortController();
    try {
      const response = await fetch(TURN_URL(), {
        method: 'POST', headers: getAuthHeaders(), body: formData, signal: abortControllerRef.current.signal,
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${response.status}`);
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let eventType = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.substring(7).trim();
          } else if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.substring(6));
              if (eventType === 'transcript') {
                questionText = data.text;
                if (data.text) setMessages((prev) => [...prev, { role: 'user', text: data.text }]);
              } else if (eventType === 'card' && data.draft) {
                consecutiveFailuresRef.current = 0;
                applyCard(data.draft);
              } else if (eventType === 'text') {
                botResponseRef.current += data.text;
                setResponseText(botResponseRef.current);
              } else if (eventType === 'audio') {
                audioQueueRef.current.push(data.audio);
                if (!isPlayingRef.current) playNextInQueue();
              } else if (eventType === 'stage' && data.stage === 'preview') {
                setStatusText(t('audio_voice_preview'));
              } else if (eventType === 'saved' && data.card) {
                savedRef.current = data;
              } else if (eventType === 'done') {
                streamDoneRef.current = true;
                if (data.reason === 'empty_transcript') {
                  consecutiveFailuresRef.current += 1;
                  if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
                    voiceStoppedByUserRef.current = true;
                  }
                } else {
                  setQuick(data.quick_replies || []);
                  pendingTurnRef.current = { question: questionText, answer: botResponseRef.current, say: data.say };
                }
                if (!isPlayingRef.current) { finishTurn(); afterTurn(); }
              } else if (eventType === 'error') {
                setStatusText(data.message || t('efd_voice_error'));
                setVoiceState('idle');
              }
            } catch (e) { /* non-JSON data line */ }
            eventType = '';
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Audio builder voice error:', err);
        setStatusText(err.message || t('efd_voice_error'));
        setVoiceState('idle');
      }
    }
  }, [language, cardId, t, playNextInQueue, finishTurn, afterTurn, applyCard]);

  /** Microphone + VAD, exactly like EfdVoiceDialog. `listen` = start right away. */
  const initListening = useCallback(async (listen) => {
    voiceStoppedByUserRef.current = false;
    consecutiveFailuresRef.current = 0;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);

      startListeningRef.current = () => {
        if (voiceStoppedByUserRef.current || !mediaStreamRef.current) return;
        setVoiceState('listening');
        setStatusText(t('efd_voice_listening'));
        speechStartTimeRef.current = null;
        silenceStartTimeRef.current = null;
        audioChunksRef.current = [];
        const mimeTypes = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
        let mimeType = '';
        for (const mt of mimeTypes) { if (MediaRecorder.isTypeSupported(mt)) { mimeType = mt; break; } }
        const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
        mediaRecorderRef.current = recorder;
        recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
        recorder.onstop = () => {
          if (recordingTimeoutRef.current) { clearTimeout(recordingTimeoutRef.current); recordingTimeoutRef.current = null; }
          if (audioChunksRef.current.length > 0) {
            sendTurn({ blob: new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' }) });
          }
        };
        recorder.start(100);
        listenStartTimeRef.current = Date.now();
        if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = setTimeout(() => {
          if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop();
        }, MAX_RECORDING_MS);

        const dataArray = new Float32Array(analyser.frequencyBinCount);
        const vadLoop = () => {
          if (voiceStoppedByUserRef.current || recorder.state !== 'recording') return;
          analyser.getFloatTimeDomainData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) sum += dataArray[i] * dataArray[i];
          const rms = Math.sqrt(sum / dataArray.length);
          setAudioLevel(rms);
          const now = Date.now();
          if (!speechStartTimeRef.current && listenStartTimeRef.current
              && now - listenStartTimeRef.current >= NO_SPEECH_TIMEOUT_MS) {
            recorder.stop();
            return;
          }
          if (rms > VAD_THRESHOLD) {
            silenceStartTimeRef.current = null;
            if (!speechStartTimeRef.current) speechStartTimeRef.current = now;
          } else if (speechStartTimeRef.current && now - speechStartTimeRef.current >= MIN_SPEECH_MS) {
            if (!silenceStartTimeRef.current) {
              silenceStartTimeRef.current = now;
            } else if (now - silenceStartTimeRef.current >= SILENCE_MS) {
              recorder.stop();
              return;
            }
          }
          vadFrameIdRef.current = requestAnimationFrame(vadLoop);
        };
        vadFrameIdRef.current = requestAnimationFrame(vadLoop);
      };
      if (listen) startListeningRef.current();
      return true;
    } catch (err) {
      console.error('Microphone error:', err);
      micDeniedRef.current = true;
      setMicDenied(true);
      setStatusText(t('efd_voice_mic_denied'));
      return false;
    }
  }, [sendTurn, t]);

  /** Stop recording without sending (a typed answer or a quick reply wins). */
  const dropRecording = useCallback(() => {
    if (vadFrameIdRef.current) cancelAnimationFrame(vadFrameIdRef.current);
    if (recordingTimeoutRef.current) { clearTimeout(recordingTimeoutRef.current); recordingTimeoutRef.current = null; }
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.onstop = null;
      recorder.stop();
    }
  }, []);

  const sendText = useCallback((text) => {
    const value = (text || '').trim();
    if (!value) return;
    dropRecording();
    voiceStoppedByUserRef.current = false;
    setTyped('');
    sendTurn({ text: value });
  }, [dropRecording, sendTurn]);

  /** Barge-in: tap while Bro thinks or speaks to interrupt and talk. */
  const bargeIn = useCallback(() => {
    if (voiceState !== 'thinking' && voiceState !== 'speaking') return;
    if (abortControllerRef.current) abortControllerRef.current.abort();
    if (currentSourceRef.current) { try { currentSourceRef.current.stop(); } catch (e) { /* noop */ } currentSourceRef.current = null; }
    if (ttsAudioContextRef.current) { try { ttsAudioContextRef.current.close(); } catch (e) { /* noop */ } ttsAudioContextRef.current = null; }
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    streamDoneRef.current = false;
    finishTurn();   // keep what was already answered, even if cut off
    if (savedRef.current) { afterTurn(); return; }
    voiceStoppedByUserRef.current = false;
    if (startListeningRef.current) startListeningRef.current(); else setVoiceState('idle');
  }, [voiceState, finishTurn, afterTurn]);

  const tapBody = useCallback(() => {
    if (voiceState === 'thinking' || voiceState === 'speaking') { bargeIn(); return; }
    if (voiceState === 'idle' && startListeningRef.current) {
      voiceStoppedByUserRef.current = false;
      consecutiveFailuresRef.current = 0;
      startListeningRef.current();
    }
  }, [voiceState, bargeIn]);

  const handleClose = useCallback(() => {
    finishTurn();
    stopVoice();
    if (onClose) onClose();
  }, [finishTurn, stopVoice, onClose]);

  // Start: microphone first (so the permission prompt comes up at once), then
  // Bro greets, then listening. Without a microphone the user types.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setVoiceState('initializing');
      setStatusText(t('efd_voice_mic_setup'));
      await initListening(false);         // without a microphone: micDeniedRef, the user types
      if (cancelled) return;
      const hello = greeting || t('audio_voice_greeting');
      setResponseText(hello);
      pendingTurnRef.current = { question: '', answer: hello, say: hello };
      streamDoneRef.current = true;
      try {
        const res = await fetch(`${API_BASE_URL}/api/fetch_speech_from_text?text=${encodeURIComponent(hello)}`
          + `&language=${encodeURIComponent(language)}`, { headers: getAuthHeaders() });
        const body = await res.json();
        if (cancelled) return;
        if (body.speech_file) {
          audioQueueRef.current.push(body.speech_file);
          playNextInQueue();
          return;
        }
      } catch (e) { /* no greeting voice — the text is there */ }
      if (cancelled) return;
      finishTurn();
      afterTurn();
    })();
    return () => { cancelled = true; stopVoice(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, responseText]);

  const interactive = voiceState === 'thinking' || voiceState === 'speaking';
  const waiting = voiceState === 'listening' || voiceState === 'idle';
  const missing = missingFields(shown);
  const done = FIELDS.length - missing.length;
  const chips = chipsOf(shown, catalog, sources, t);

  return (
    <div className="av-overlay">
      <IconButton onClick={handleClose} size="small" className="av-close" aria-label={t('close') || 'Close'}
        sx={{ position: 'absolute', top: 10, right: 10, color: 'text.secondary', bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
        <CloseIcon />
      </IconButton>

      <div className="av-body ac-voice-body" onClick={tapBody} style={{ cursor: interactive || (voiceState === 'idle' && !micDenied) ? 'pointer' : 'default' }}>
        <div className="av-blob-wrap">
          <div className={`av-blob av-${voiceState}`} style={{ transform: `scale(${1 + (voiceState === 'listening' ? audioLevel : 0) * 0.3})` }}>
            <div className="av-blob-inner" />
            <div className="av-blob-ring r1" />
            <div className="av-blob-ring r2" />
          </div>
        </div>

        <div className="av-status">{statusText}</div>
        {responseText && <div className="av-response ac-response">{responseText}</div>}
        {interactive && <div className="av-hint">{t('efd_voice_interrupt')}</div>}

        {/* The card so far — progress and the collected fields (Client Notes pattern). */}
        <Box className="ac-collected" onClick={(e) => e.stopPropagation()}>
          <Box sx={{ width: '80%', maxWidth: 320 }}>
            <LinearProgress variant="determinate" value={(done / FIELDS.length) * 100}
              sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)', '& .MuiLinearProgress-bar': { borderRadius: 3 } }} />
            <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block', textAlign: 'center' }}>
              {missing.length
                ? t('audio_progress', { done, total: FIELDS.length, next: t(`audio_field_${missing[0]}`) })
                : t('audio_progress_complete')}
            </Typography>
          </Box>
          {chips.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.75, px: 2, width: '100%', maxWidth: 640 }}>
              {chips.map(([key, label]) => (
                <Chip key={key} label={label} size="small" className={fresh.has(key) ? 'ac-chip-fresh' : ''}
                  sx={{ height: 'auto', maxWidth: '100%', bgcolor: 'rgba(255,255,255,0.12)', color: 'text.primary', fontSize: '0.7rem',
                    '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5, lineHeight: 1.35 } }} />
              ))}
            </Box>
          )}
        </Box>

        {/* Answer without speaking: quick replies and a text field. */}
        {waiting && (
          <Box className="ac-answer" onClick={(e) => e.stopPropagation()}>
            {quick.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.75 }}>
                {quick.map((q) => (
                  <Chip key={q} label={q} size="small" icon={<MicNoneIcon />} onClick={() => sendText(q)} variant="outlined"
                    sx={{ borderColor: 'rgba(25, 208, 239, 0.45)', color: '#e6f7fb', bgcolor: 'rgba(25, 208, 239, 0.08)',
                      '& .MuiChip-icon': { color: 'primary.main', fontSize: 15 },
                      '&:hover': { bgcolor: 'rgba(25, 208, 239, 0.16)' } }} />
                ))}
              </Box>
            )}
            <Box component="form" onSubmit={(e) => { e.preventDefault(); sendText(typed); }} className="ac-type">
              <InputBase value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={t('audio_voice_type_placeholder')}
                inputProps={{ 'aria-label': t('audio_voice_type_placeholder') }} sx={{ flex: 1, fontSize: 13, color: 'text.primary' }} />
              <IconButton type="submit" size="small" disabled={!typed.trim()} aria-label={t('send_tooltip') || 'Send'} sx={{ color: 'primary.main' }}>
                <SendIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
        )}
      </div>

      {messages.length > 0 && (
        <div className="av-transcript" ref={transcriptRef}>
          {messages.map((m, i) => (
            <div key={i} className={`av-msg av-msg-${m.role}`}>
              <span className="av-role">{m.role === 'user' ? t('are_voice_you') : t('are_voice_assistant')}</span>
              <span className="av-text ac-msg-text">{m.text}</span>
            </div>
          ))}
        </div>
      )}

      <AreDisclaimer sx={{ flexShrink: 0, justifyContent: 'center', textAlign: 'center', pb: 1 }} />
    </div>
  );
}
